import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useEffect } from 'react';
import { useCustomIntl } from '../../../../../i18n/i18n';
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks';
import {
  closeConfigurationDialog,
  DialogMode,
  getDialogMode,
  isConfigurationDialogOpen,
} from '../../../state/onboardingUiSlice';
import { DialogFooter } from '../DialogFooter';
import {
  createServiceConfigurationFormData,
  ServiceConfigurationForm,
} from './ServiceConfigurationForm';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import { useUpdateServiceActivationsV2Mutation } from '../../../../../codegen/vehicles';
import { RemoveConfirmationDialog } from '../removeConfirmationDialog/RemoveConfirmationDialog';
import {
  useLoadedVehicles,
  useSelectedVehicle,
} from '../../../../../state/vehicles/useVehicles';
import { ServiceConfigurationDialogBody } from './ServiceConfigurationDialogBody';
import { some } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';

export const DIALOG_DATA_COLOR = 'text-color-dark';

export const ServiceConfigurationDialog = () => {
  const dispatch = useAppDispatch();
  const selectedVehicle = useSelectedVehicle();
  const { updateVehicle } = useLoadedVehicles();
  const intl = useCustomIntl();
  const dialogMode = useAppSelector(getDialogMode);
  const isDialogOpen = useAppSelector(isConfigurationDialogOpen);
  const formUtils = useForm<ServiceConfigurationForm>();
  const [
    updateServiceActivations,
    { isLoading: isUpdateServiceActivationLoading },
  ] = useUpdateServiceActivationsV2Mutation();

  useEffect(() => {
    formUtils.reset(createServiceConfigurationFormData(selectedVehicle));
  }, [selectedVehicle]);

  const formData = formUtils.watch();

  const closeDialog = () => {
    dispatch(closeConfigurationDialog());
  };

  const handleSave = async () => {
    await submitServiceActivations();
    closeDialog();
  };

  const submitServiceActivations = async () => {
    map(formData.serviceActivations, (serviceActivation) => {
      if (
        isEmpty(serviceActivation.paymentMethodAssignments) &&
        serviceActivation.enabled
      ) {
        serviceActivation.enabled = false;
      }
      if (!serviceActivation.enabled) {
        serviceActivation.paymentMethodAssignments = [];
      }
    });

    const result = await updateServiceActivations({
      vehicleId: selectedVehicle!.id,
      updateServiceActivationsRequest: {
        serviceActivations: Object.values(formData.serviceActivations),
      },
    });

    if ('error' in result) {
      throw Error(result.error);
    }

    formUtils.reset(formData);

    updateVehicle(result.data.id, (_) => {
      return result.data;
    });
  };

  const isServiceActivationsDirty = some(
    formUtils.formState.dirtyFields.serviceActivations,
    (serviceActivation) => {
      return (
        serviceActivation.enabled ||
        !!serviceActivation.paymentMethodAssignments
      );
    },
  );

  const showDialog = isDialogOpen && !!selectedVehicle;

  const showFirstCardTitle = dialogMode === DialogMode.FIRST_SERVICE_CARD;

  return (
    <FormProvider {...formUtils}>
      <Dialog
        data-testid={'service-configuration-dialog'}
        useOverflow
        bsSize={Dialog.SIZE_SM}
        show={showDialog}
        onClose={closeDialog}
        title={intl.formatMessage({
          id: showFirstCardTitle
            ? 'onboarding.dialog.firstCard.title'
            : 'onboarding.dialog.title',
        })}
        body={<ServiceConfigurationDialogBody dialogMode={dialogMode} />}
        footer={
          <DialogFooter
            dialogMode={dialogMode}
            showLegalText
            isSaveButtonEnabled={isServiceActivationsDirty}
            isLoading={isUpdateServiceActivationLoading}
            onClose={closeDialog}
            onSave={handleSave}
          />
        }
      />
      <RemoveConfirmationDialog />
    </FormProvider>
  );
};
